/**
 * File attachment utilities for QPilot Chatbot
 */

// File type configurations
export const FILE_RULES = {
  images: {
    extensions: ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    maxSize: 10 * 1024 * 1024, // 10MB
    mimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
  },
  documents: {
    extensions: ['.pdf', '.csv', '.xlsx', '.txt'],
    maxSize: 20 * 1024 * 1024, // 20MB
    mimeTypes: ['application/pdf', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain']
  },
  code: {
    extensions: ['.py', '.json', '.md'],
    maxSize: 5 * 1024 * 1024, // 5MB
    mimeTypes: ['text/x-python-script', 'application/json', 'text/markdown', 'text/plain']
  }
};

export const ATTACHMENT_LIMITS = {
  maxFiles: 5,
  maxTotalSize: 25 * 1024 * 1024 // 25MB
};

/**
 * Get file category based on extension or mime type
 */
export const getFileCategory = (file) => {
  const extension = '.' + file.name.split('.').pop().toLowerCase();
  const mimeType = file.type.toLowerCase();

  for (const [category, rules] of Object.entries(FILE_RULES)) {
    if (rules.extensions.includes(extension) || rules.mimeTypes.includes(mimeType)) {
      return category;
    }
  }
  return null;
};

/**
 * Validate a single file
 */
export const validateFile = (file, options = {}) => {
  const category = getFileCategory(file);
  const allowedCategories = options.allowedCategories;
  
  if (!category) {
    return {
      valid: false,
      error: `Unsupported file type: ${file.name}. Supported types: images (png, jpg, jpeg, gif, webp), documents (pdf, csv, xlsx, txt), code (py, json, md)`
    };
  }

  if (Array.isArray(allowedCategories) && allowedCategories.length > 0 && !allowedCategories.includes(category)) {
    const categoryLabel = allowedCategories.join(', ');
    return {
      valid: false,
      error: `Unsupported file type: ${file.name}. This uploader only accepts: ${categoryLabel}`
    };
  }

  const rules = FILE_RULES[category];
  if (file.size > rules.maxSize) {
    const maxSizeMB = rules.maxSize / (1024 * 1024);
    return {
      valid: false,
      error: `File ${file.name} is too large. Maximum size for ${category} is ${maxSizeMB}MB`
    };
  }

  return { valid: true, category };
};

/**
 * Validate multiple files
 */
export const validateFiles = (files, existingFiles = [], options = {}) => {
  const allFiles = [...existingFiles, ...files];
  const maxFiles = options.maxFiles ?? ATTACHMENT_LIMITS.maxFiles;
  const maxTotalSize = options.maxTotalSize ?? ATTACHMENT_LIMITS.maxTotalSize;
  
  // Check total file count
  if (allFiles.length > maxFiles) {
    return {
      valid: false,
      error: `Maximum ${maxFiles} attachments allowed`
    };
  }

  // Check total size
  const totalSize = allFiles.reduce((sum, file) => sum + (file.size || file.file?.size || 0), 0);
  if (totalSize > maxTotalSize) {
    const maxSizeMB = maxTotalSize / (1024 * 1024);
    return {
      valid: false,
      error: `Total attachment size cannot exceed ${maxSizeMB}MB`
    };
  }

  // Validate each individual file
  for (const file of files) {
    const validation = validateFile(file, options);
    if (!validation.valid) {
      return validation;
    }
  }

  return { valid: true };
};

/**
 * Check if file is an image
 */
export const isImageFile = (file) => {
  const category = getFileCategory(file);
  return category === 'images';
};

/**
 * Generate unique file ID
 */
export const generateFileId = () => {
  return `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create object URL for preview (images only)
 */
export const createPreviewUrl = (file) => {
  if (isImageFile(file)) {
    return URL.createObjectURL(file);
  }
  return null;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file icon based on extension
 */
export const getFileIcon = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  
  switch (extension) {
    case 'pdf':
      return 'FiFileText';
    case 'csv':
    case 'xlsx':
      return 'FiGrid';
    case 'txt':
      return 'FiFile';
    case 'py':
      return 'FiCode';
    case 'json':
      return 'FiCode';
    case 'md':
      return 'FiFile';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
      return 'FiImage';
    default:
      return 'FiFile';
  }
};

/**
 * Clean up object URLs to prevent memory leaks
 */
export const cleanupPreviewUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};
